import {LitElement, css, html} from 'lit';
import {customElement, property} from 'lit/decorators.js';

type Item = {
  label: string;
  type: string;
  [propName: string]: any
  properties: any;
};

@customElement('artist-list-item')
export class ArtistListItem extends LitElement {

  @property()
  item?: Item;

  @property({attribute: 'scroll-to-element'})
  scrollToElement?: boolean = true

  private get itemObject() {
    return this.item?.properties?.artist?.artist || this.item?.properties?.artist?.host;
  }

  _scrollToAnchor() {
    if (!this.scrollToElement) return
    let hash = location.hash
    if (hash) {
      let target = this.renderRoot?.querySelector(hash) ?? null;
      if (target) {
        target.setAttribute('open', 'open')
        target.scrollIntoView({
          behavior: 'smooth'
        });
        /* Remove Hash after scroll *//*
        if(window.history.pushState) {
          window.history.pushState('', '/', window.location.pathname)
        } else {
          window.location.hash = '';
        }*/
      }
    }
  }

  firstUpdated() {
    this._scrollToAnchor()
    this._initHashChange()
  }

  _initHashChange() {
    window.addEventListener("hashchange", () => {
      this._scrollToAnchor()
    });
  }

  createRenderRoot() {
    return this;
  }

  render() {
    return html`
        <div id="${this.item?.properties.identifier}" class="artist-item group artist-item__type-artist">
            <div class="artist-item__img">
                ${this.itemObject.image
                        ? html` <img src=${this.itemObject.image?.absoluteWebPath}
                                     alt="${this.item?.label}" class="w-full h-full object-cover object-top">`
                        : null
                }
            </div>
            <div class="artist-item__social-media">
                ${this.itemObject.socialMedia?.instagram
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.instagram}" aria-label="instagram" target="_blank">
                            <svg aria-hidden="true" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                                <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.facebook
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.facebook}" aria-label="facebook" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.tiktok
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.tiktok}" aria-label="tiktok" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.twitter
                    ? html`
                        <a href="${this.itemObject.socialMedia?.twitter}" aria-label="twitter" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 271" style="enable-background:new 0 0 300 271;" xml:space="preserve">
                                <path d="M236,0h46L181,115l118,156h-92.6l-72.5-94.8l-83,94.8h-46l107-123L-1.1,0h94.9l65.5,86.6L236,0z M219.9,244h25.5L80.4,26H53 L219.9,244z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.youtube
                    ? html`
                        <a href="${this.itemObject.socialMedia?.youtube}" aria-label="youtube" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                                <path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.spotify
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.spotify}" aria-label="spotify" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512">
                                <path d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-62.5-37-155.7-51.9-238.6-29.4-4.8 1.3-7.4 2.6-11.9 2.6-10.7 0-19.4-8.7-19.4-19.4s5.2-17.8 15.5-20.7c27.8-7.8 56.2-13.6 97.8-13.6 64.9 0 127.6 16.1 177 45.5 8.1 4.8 11.3 11 11.3 19.7-.1 10.8-8.5 19.5-19.4 19.5zm31-76.2c-5.2 0-8.4-1.3-12.9-3.9-71.2-42.5-198.5-52.7-280.9-29.7-3.6 1-8.1 2.6-12.9 2.6-13.2 0-23.3-10.3-23.3-23.6 0-13.6 8.4-21.3 17.4-23.9 35.2-10.3 74.6-15.2 117.5-15.2 73 0 149.5 15.2 205.4 47.8 7.8 4.5 12.9 10.7 12.9 22.6 0 13.6-11 23.3-23.2 23.3z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.appleMusic
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.appleMusic}" aria-label="appleMusic" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 501.7 498.2">
                                <path d="M500.9,137.7c0-5,0-10.1-.2-15.1-.3-11-.9-22-2.9-32.8-2-11-5.2-21.2-10.3-31.2-5-9.8-11.5-18.8-19.3-26.6-7.8-7.8-16.8-14.3-26.6-19.3-10-5.1-20.2-8.3-31.2-10.3-10.8-1.9-21.9-2.6-32.8-2.9-5,0-10.1-.2-15.1-.2h-205.1c-6.1-.1-12-.1-18-.1s-10.1,0-15.1.2c-11,.3-22,.9-32.8,2.9-11,2-21.2,5.2-31.2,10.3-9.8,5-18.8,11.5-26.6,19.3-7.8,7.8-14.3,16.8-19.3,26.6-5.1,10-8.3,20.2-10.3,31.2-1.9,10.8-2.6,21.9-2.9,32.8,0,5-.2,10.1-.2,15.1-.1,6.1-.1,12-.1,18v205.1c0,5,0,10.1.2,15.1.3,11,.9,22,2.9,32.8,2,11,5.2,21.2,10.3,31.2,5,9.8,11.5,18.8,19.3,26.6,7.8,7.8,16.8,14.3,26.6,19.3,10,5.1,20.2,8.3,31.2,10.3,10.8,1.9,21.9,2.6,32.8,2.9,5,.1,10.1.2,15.1.2h223c5,0,10.1,0,15.1-.2,11-.3,22-.9,32.8-2.9,11-2,21.2-5.2,31.2-10.3,9.8-5,18.8-11.5,26.6-19.3,7.8-7.8,14.3-16.8,19.3-26.6,5.1-10,8.3-20.2,10.3-31.2,1.9-10.8,2.6-21.9,2.9-32.8.1-5,.2-10.1.2-15.1v-223h.2ZM366.9,324.6c0,6.3,0,12.1-1.4,18.4-1.3,6.2-3.7,11.9-7.3,17.2-3.6,5.2-8.3,9.5-13.7,12.7-5.5,3.2-11.2,5.1-17.4,6.3-11.5,2.3-19.4,2.8-26.8,1.4-7.1-1.4-13.2-4.7-18-9.2-7.2-6.6-11.7-15.5-12.6-24.8-1.1-10.9,2.5-22.5,10.7-31.1,4.1-4.3,9.3-7.7,16.2-10.4,7.2-2.8,15.2-4.5,27.5-7,3.2-.7,6.5-1.3,9.7-2,4.3-.9,7.9-1.9,10.8-5.5,3-3.6,3-8,3-12.4v-110.1c0-8.4-3.8-10.7-11.8-9.2-5.8,1.1-129.3,26-129.3,26-7,1.7-9.4,4-9.4,12.6v161.3c0,6.3-.3,12.1-1.7,18.4-1.3,6.2-3.7,11.9-7.3,17.2-3.6,5.2-8.3,9.5-13.7,12.7-5.5,3.2-11.2,5.2-17.4,6.4-11.5,2.3-19.4,2.8-26.8,1.4-7.1-1.4-13.2-4.8-18-9.3-7.2-6.6-11.3-15.5-12.3-24.8-1.1-10.9,2.2-22.5,10.4-31.1,4.1-4.3,9.3-7.7,16.2-10.4,7.2-2.8,15.2-4.5,27.5-7,3.2-.7,6.5-1.3,9.7-2,4.3-.9,7.9-1.9,10.8-5.5,2.9-3.6,3.3-7.8,3.3-12.2V126.7c0-2.5.2-4.2.3-5,.6-3.9,2.2-7.3,5-9.7,2.4-2,5.4-3.3,9.3-4.2h0l148.6-30c1.3-.3,12-2.2,13.2-2.3,8-.7,12.5,4.6,12.5,13.1v235.8h0l.2.2Z"></path>                            
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.soundcloud
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.soundcloud}" aria-label="soundcloud" target="_blank">
                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                                <path d="M111.4 256.3l5.8 65-5.8 68.3c-.3 2.5-2.2 4.4-4.4 4.4s-4.2-1.9-4.2-4.4l-5.6-68.3 5.6-65c0-2.2 1.9-4.2 4.2-4.2 2.2 0 4.1 2 4.4 4.2zm21.4-45.6c-2.8 0-4.7 2.2-5 5l-5 105.6 5 68.3c.3 2.8 2.2 5 5 5 2.5 0 4.7-2.2 4.7-5l5.8-68.3-5.8-105.6c0-2.8-2.2-5-4.7-5zm25.5-24.1c-3.1 0-5.3 2.2-5.6 5.3l-4.4 130 4.4 67.8c.3 3.1 2.5 5.3 5.6 5.3 2.8 0 5.3-2.2 5.3-5.3l5.3-67.8-5.3-130c0-3.1-2.5-5.3-5.3-5.3zM7.2 283.2c-1.4 0-2.2 1.1-2.5 2.5L0 321.3l4.7 35c.3 1.4 1.1 2.5 2.5 2.5s2.2-1.1 2.5-2.5l5.6-35-5.6-35.6c-.3-1.4-1.1-2.5-2.5-2.5zm23.6-21.9c-1.4 0-2.5 1.1-2.5 2.5l-6.4 57.5 6.4 56.1c0 1.7 1.1 2.8 2.5 2.8s2.5-1.1 2.8-2.5l7.2-56.4-7.2-57.5c-.3-1.4-1.4-2.5-2.8-2.5zm25.3-11.4c-1.7 0-3.1 1.4-3.3 3.3L47 321.3l5.8 65.8c.3 1.7 1.7 3.1 3.3 3.1 1.7 0 3.1-1.4 3.1-3.1l6.9-65.8-6.9-68.1c0-1.9-1.4-3.3-3.1-3.3zm25.3-2.2c-1.9 0-3.6 1.4-3.6 3.6l-5.8 70 5.8 67.8c0 2.2 1.7 3.6 3.6 3.6s3.6-1.4 3.9-3.6l6.4-67.8-6.4-70c-.3-2.2-2-3.6-3.9-3.6zm241.4-110.9c-1.1-.8-2.8-1.4-4.2-1.4-2.2 0-4.2.8-5.6 1.9-1.9 1.7-3.1 4.2-3.3 6.7v.8l-3.3 176.7 1.7 32.5 1.7 31.7c.3 4.7 4.2 8.6 8.9 8.6s8.6-3.9 8.6-8.6l3.9-64.2-3.9-177.5c-.4-3-2-5.8-4.5-7.2zm-26.7 15.3c-1.4-.8-2.8-1.4-4.4-1.4s-3.1.6-4.4 1.4c-2.2 1.4-3.6 3.9-3.6 6.7l-.3 1.7-2.8 160.8s0 .3 3.1 65.6v.3c0 1.7.6 3.3 1.7 4.7 1.7 1.9 3.9 3.1 6.4 3.1 2.2 0 4.2-1.1 5.6-2.5 1.7-1.4 2.5-3.3 2.5-5.6l.3-6.7 3.1-58.6-3.3-162.8c-.3-2.8-1.7-5.3-3.9-6.7zm-111.4 22.5c-3.1 0-5.8 2.8-5.8 6.1l-4.4 140.6 4.4 67.2c.3 3.3 2.8 5.8 5.8 5.8 3.3 0 5.8-2.5 6.1-5.8l5-67.2-5-140.6c-.2-3.3-2.7-6.1-6.1-6.1zm376.7 62.8c-10.8 0-21.1 2.2-30.6 6.1-6.4-70.8-65.8-126.4-138.3-126.4-17.8 0-35 3.3-50.3 9.4-6.1 2.2-7.8 4.4-7.8 9.2v249.7c0 5 3.9 8.6 8.6 9.2h218.3c43.3 0 78.6-35 78.6-78.3.1-43.6-35.2-78.9-78.5-78.9zm-296.7-60.3c-4.2 0-7.5 3.3-7.8 7.8l-3.3 136.7 3.3 65.6c.3 4.2 3.6 7.5 7.8 7.5 4.2 0 7.5-3.3 7.5-7.5l3.9-65.6-3.9-136.7c-.3-4.5-3.3-7.8-7.5-7.8zm-53.6-7.8c-3.3 0-6.4 3.1-6.4 6.7l-3.9 145.3 3.9 66.9c.3 3.6 3.1 6.4 6.4 6.4 3.6 0 6.4-2.8 6.7-6.4l4.4-66.9-4.4-145.3c-.3-3.6-3.1-6.7-6.7-6.7zm26.7 3.4c-3.9 0-6.9 3.1-6.9 6.9L227 321.3l3.9 66.4c.3 3.9 3.1 6.9 6.9 6.9s6.9-3.1 6.9-6.9l4.2-66.4-4.2-141.7c0-3.9-3-6.9-6.9-6.9z"></path>
                            </svg>
                        </a>`
                    : null
                }
                ${this.itemObject.socialMedia?.website
                    ? html` 
                        <a href="${this.itemObject.socialMedia?.website}" aria-label="website" target="_blank">
                            <svg aria-hidden="true" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                                <path d="M352 256c0 22.2-1.2 43.6-3.3 64l-185.3 0c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64l185.3 0c2.2 20.4 3.3 41.8 3.3 64zm28.8-64l123.1 0c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64l-123.1 0c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32l-116.7 0c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0l-176.6 0c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0L18.6 160C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192l123.1 0c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64L8.1 320C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6l176.6 0c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352l116.7 0zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6l116.7 0z"/>
                            </svg>
                        </a>`
                    : null
                }
            </div>
            <div class="artist-item__body">
                
                ${this.item?.type == 'artist'
                    ? html`

                        ${this.item?.properties?.artist?.days && this.item?.properties?.artist?.days.length > 0
                            ? html`
                                <div class="artist-item__days">
                                    ${this.item?.properties?.artist?.days.map((day: any) => html`
                                        <div class="artist-item__day">${day.name}</div>
                                    `)}
                                </div>
                            `
                            : null
                        }
                          
                            <div class="artist-item__name ${this.itemObject?.nameUppercase ? 'uppercase' : ''}">
                                ${this.itemObject?.name}

                                ${this.item?.properties?.artist?.subname !== ''
                                    ? html`
                                        <span class="artist-item__subname ${this.item?.properties?.artist?.subnameUppercase ? 'uppercase' : ''}">
                                            ${this.item?.properties?.artist?.subname}
                                        </span>
                                    `: null
                                }

                            </div>

                            ${this.item?.properties?.artist?.stages && this.item?.properties?.artist?.stages.length > 0
                                ? html`
                                    <div class="artist-item__stages">
                                        ${this.item?.properties?.artist?.stages.map((stage: any) => html`
                                            <div class="artist-item__stage">${stage.name}</div>
                                        `)}
                                    </div>
                                `
                                : null
                            }

                            ${this.item?.properties?.artist?.shows && this.item?.properties?.artist?.shows.length > 0
                                ? html`
                        <div class="artist-item__shows">
                            ${this.item?.properties?.artist?.shows.map((show: any) => html`
                                <div class="artist-item__show">
                                    <span class="text-green font-black">> </span>
                                    <span class="text-green">${show.day.name}</span> //
                                    <span>${show.stage.name}</span>
                                </div>
                            `)}
                        </div>
                    `
                                : null
                            }
                  
                            
                      `: null
                }
                
                ${this.item?.type == 'host'
                    ? html`

                        <div class="host-item__name ${this.itemObject?.nameUppercase ? 'uppercase' : ''}">
                            
                            ${this.item?.properties?.artist?.stages && this.item?.properties?.artist?.stages.length > 0
                                ? html`
                                    <div class="host-item__stages">
                                        ${this.item?.properties?.artist?.stages.map((stage: any) => html`
                                        <div class="host-item__stage">${stage.name}</div>
                                    `)}
                                    </div>
                                `
                                : null
                            }
                            
                            <div>hosted by</div>
                            
                        </div>
                          
                        <div class="artist-item__logo">
                            ${this.itemObject.logo
                                ? html` <img src=${this.itemObject.logo?.absoluteWebPath} alt="${this.item?.label}" class="">`
                                : null
                            }
                        </div>
                            
                    `: null
                }
                
                ${this.itemObject?.detailPageUrl
                    ? html`
                        <div class="artist-item__link">
                            <svg aria-hidden="true" class="fill-current w-6 h-6 mx-auto" viewBox="0 0 448 512"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path d="M240.971 130.524l194.343 194.343c9.373 9.373 9.373 24.569 0 33.941l-22.667 22.667c-9.357 9.357-24.522 9.375-33.901.04L224 227.495 69.255 381.516c-9.379 9.335-24.544 9.317-33.901-.04l-22.667-22.667c-9.373-9.373-9.373-24.569 0-33.941L207.03 130.525c9.372-9.373 24.568-9.373 33.941-.001z"></path>
                            </svg>
                            <a href="${this.itemObject?.detailPageUrl}"
                               class="btn btn-md">
                                Read more
                            </a>
                        </div>
                    `
                    : null
                }

            </div>
        </div>
    `;
  }

  static styles = css`
      :host {
          display: block;
          font-family: inherit;

          --inner-padding: clamp(1.25rem, 2vw, 2.5rem);
          --inner-padding-half: clamp(0.75rem, 1vw, 1.25rem);
      }

      .hidden {
          display: none;
      }

      a {
          color: var(--color-green, #33cc99);
          transition-property: color;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
          text-decoration: none;
      }

      a:hover {
          color: var(--color-yellow, #33cc99);
      }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'artist-list-item': ArtistListItem
  }
}
